import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Wifi, Zap, Star, Shield, Timer } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "Push, Don't Pull",
        icon: "📲",
        content: (
            <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-2xl border-2 border-blue-200">
                    <h3 className="font-bold text-xl text-blue-800 mb-3">Don't Call Us, We'll Call You</h3>
                    <p className="text-slate-700 mb-4">
                        Imagine ordering a pizza.
                    </p>
                    <ul className="space-y-4">
                        <li className="bg-white p-4 rounded-xl border border-red-200 opacity-60">
                            <strong>❌ Polling:</strong> Calling the pizza place every 10 seconds: "Is it ready? Is it ready?" (Annoying!)
                        </li>
                        <li className="bg-white p-4 rounded-xl border border-green-200 shadow-md">
                            <strong>✅ Webhook:</strong> The pizza place calls YOU when it's ready. (Efficient!)
                        </li>
                    </ul>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "The Payload",
        icon: "📦",
        content: (
            <div className="space-y-6">
                <p className="text-slate-600">Webhooks deliver a package of data called a <strong>Payload</strong>.</p>
                <div className="bg-slate-900 text-green-400 p-6 rounded-xl font-mono text-sm shadow-xl">
                    <div>{'{'}</div>
                    <div className="pl-4 text-purple-400">"event": <span className="text-green-300">"payment.success"</span>,</div>
                    <div className="pl-4 text-purple-400">"data": {'{'}</div>
                    <div className="pl-8 text-blue-300">"amount": 50,</div>
                    <div className="pl-8 text-blue-300">"currency": "USD"</div>
                    <div className="pl-4">{'}'}</div>
                    <div>{'}'}</div>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Mission Brief",
        icon: "🧤",
        content: (
            <div className="space-y-6">
                <div className="bg-orange-50 p-6 rounded-2xl border-2 border-orange-200">
                    <h3 className="font-bold text-xl text-orange-800 mb-3">Webhook Catcher</h3>
                    <p className="text-slate-700 mb-4">
                        Your server needs to "catch" these payloads as they arrive.
                    </p>
                    <p className="font-bold text-orange-900">
                        Score 50 points to prove your server is ready for production!
                    </p>
                </div>
                <div className="flex gap-4 text-sm">
                    <div className="flex items-center gap-2"><span className="text-xl">⚡</span> Speed Up</div>
                    <div className="flex items-center gap-2"><span className="text-xl">🛡️</span> Shield</div>
                    <div className="flex items-center gap-2"><span className="text-xl">⭐</span> Double Points</div>
                </div>
            </div>
        )
    }
];

// ============ GAME LOGIC ============
interface WebhookPayload {
    id: number;
    type: string;
    data: object;
    x: number;
    caught: boolean | null;
    isPowerUp?: boolean;
    powerUpType?: 'double' | 'shield' | 'slow';
    points: number;
}

const PAYLOAD_TYPES = [
    { type: 'user.created', data: { id: 123, email: 'gorilla@jungle.com' }, color: 'bg-green-500', points: 10 },
    { type: 'order.placed', data: { orderId: 456, amount: 99.99 }, color: 'bg-blue-500', points: 10 },
    { type: 'payment.failed', data: { error: 'Card declined' }, color: 'bg-red-500', points: 15 },
    { type: 'message.received', data: { from: 'Gorilla Scout', text: 'Bananas!' }, color: 'bg-yellow-500', points: 10 },
    { type: 'subscription.renewed', data: { plan: 'Pro', amount: 29 }, color: 'bg-purple-500', points: 20 },
];

const POWER_UPS = [
    { type: 'double', emoji: '⭐', color: 'bg-yellow-400', label: '2X Points' },
    { type: 'shield', emoji: '🛡️', color: 'bg-blue-400', label: 'Extra Life' },
    { type: 'slow', emoji: '⏱️', color: 'bg-cyan-400', label: 'Slow Time' },
];

type Difficulty = 'easy' | 'medium' | 'hard';

const DIFFICULTY_SETTINGS = {
    easy: { spawnRate: 2500, fallDuration: 3, catcherWidth: 20 },
    medium: { spawnRate: 2000, fallDuration: 2.5, catcherWidth: 15 },
    hard: { spawnRate: 1500, fallDuration: 2, catcherWidth: 12 },
};

const WebhookGame = ({ onComplete }: { onComplete: () => void }) => {
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(() => {
        const saved = localStorage.getItem('webhook-highscore');
        return saved ? parseInt(saved) : 0;
    });
    const [lives, setLives] = useState(3);
    const [payloads, setPayloads] = useState<WebhookPayload[]>([]);
    const [isPlaying, setIsPlaying] = useState(false);
    const [catcherX, setCatcherX] = useState(50);
    const [gameOver, setGameOver] = useState(false);
    const [difficulty, setDifficulty] = useState<Difficulty>('medium');
    const [multiplier, setMultiplier] = useState(1);
    const [slowMode, setSlowMode] = useState(false);
    const [combo, setCombo] = useState(0);
    const [showCombo, setShowCombo] = useState(false);

    const settings = DIFFICULTY_SETTINGS[difficulty];

    const spawnPayload = useCallback(() => {
        const isPowerUp = Math.random() < 0.2;

        if (isPowerUp) {
            const powerUp = POWER_UPS[Math.floor(Math.random() * POWER_UPS.length)];
            const newPayload: WebhookPayload = {
                id: Date.now(),
                type: powerUp.label,
                data: {},
                x: Math.random() * 70 + 15,
                caught: null,
                isPowerUp: true,
                powerUpType: powerUp.type as 'double' | 'shield' | 'slow',
                points: 0,
            };
            setPayloads(prev => [...prev, newPayload]);
        } else {
            const template = PAYLOAD_TYPES[Math.floor(Math.random() * PAYLOAD_TYPES.length)];
            const newPayload: WebhookPayload = {
                id: Date.now(),
                type: template.type,
                data: template.data,
                x: Math.random() * 70 + 15,
                caught: null,
                points: template.points,
            };
            setPayloads(prev => [...prev, newPayload]);
        }
    }, []);

    useEffect(() => {
        if (!isPlaying) return;
        const rate = slowMode ? settings.spawnRate * 1.5 : settings.spawnRate;
        const interval = setInterval(spawnPayload, rate);
        return () => clearInterval(interval);
    }, [isPlaying, spawnPayload, slowMode, settings.spawnRate]);

    useEffect(() => {
        if (lives <= 0) {
            setGameOver(true);
            setIsPlaying(false);
            if (score > highScore) {
                setHighScore(score);
                localStorage.setItem('webhook-highscore', score.toString());
            }
        }
    }, [lives, score, highScore]);

    useEffect(() => {
        if (slowMode) {
            const timer = setTimeout(() => setSlowMode(false), 5000);
            return () => clearTimeout(timer);
        }
    }, [slowMode]);

    useEffect(() => {
        if (multiplier > 1) {
            const timer = setTimeout(() => setMultiplier(1), 8000);
            return () => clearTimeout(timer);
        }
    }, [multiplier]);

    const handleCatch = (payload: WebhookPayload) => {
        const catcherWidth = settings.catcherWidth;
        const catcherLeft = catcherX - catcherWidth;
        const catcherRight = catcherX + catcherWidth;
        const isCaught = payload.x >= catcherLeft && payload.x <= catcherRight;

        setPayloads(prev => prev.map(p =>
            p.id === payload.id ? { ...p, caught: isCaught } : p
        ));

        if (isCaught) {
            if (payload.isPowerUp) {
                switch (payload.powerUpType) {
                    case 'double': setMultiplier(2); break;
                    case 'shield': setLives(l => l + 1); break;
                    case 'slow': setSlowMode(true); break;
                }
            } else {
                const points = payload.points * multiplier;
                setScore(s => s + points);
                setCombo(c => c + 1);
                setShowCombo(true);
                setTimeout(() => setShowCombo(false), 500);

                if (score + points >= 50) {
                    // Check win condition
                    // We don't stop the game immediately, but trigger the callback
                    onComplete();
                }
            }
        } else {
            if (!payload.isPowerUp) {
                setLives(l => l - 1);
                setCombo(0);
            }
        }

        setTimeout(() => {
            setPayloads(prev => prev.filter(p => p.id !== payload.id));
        }, 100);
    };

    const startGame = () => {
        setIsPlaying(true);
        setScore(0);
        setLives(3);
        setPayloads([]);
        setGameOver(false);
        setMultiplier(1);
        setSlowMode(false);
        setCombo(0);
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        setCatcherX(Math.min(Math.max(x, 10), 90));
    };

    return (
        <div className="bg-slate-900 rounded-3xl p-8 shadow-xl min-h-[600px] flex flex-col items-center relative overflow-hidden font-sans text-slate-100">
            {/* Difficulty Selector */}
            {!isPlaying && !gameOver && (
                <div className="flex justify-center gap-4 mb-6 relative z-10">
                    {(['easy', 'medium', 'hard'] as Difficulty[]).map(d => (
                        <button
                            key={d}
                            onClick={() => setDifficulty(d)}
                            className={`px-6 py-2 rounded-xl font-bold capitalize transition-all ${difficulty === d
                                ? d === 'easy' ? 'bg-green-500 text-white' : d === 'medium' ? 'bg-yellow-500 text-black' : 'bg-red-500 text-white'
                                : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                                }`}
                        >
                            {d}
                        </button>
                    ))}
                </div>
            )}

            {/* Score Board */}
            <div className="flex justify-center gap-4 mb-8 flex-wrap relative z-10">
                <div className="bg-slate-800 px-6 py-3 rounded-xl border border-slate-700 text-center">
                    <div className="text-2xl font-black text-green-400">{score}</div>
                    <div className="text-xs text-slate-500 font-bold">Score</div>
                </div>
                <div className="bg-slate-800 px-6 py-3 rounded-xl border border-slate-700 text-center">
                    <div className="text-2xl font-black text-red-400 flex items-center justify-center gap-1">
                        {Array(lives).fill('❤️').join('')}{Array(Math.max(0, 3 - lives)).fill('🖤').join('')}
                    </div>
                    <div className="text-xs text-slate-500 font-bold">Lives</div>
                </div>
                {multiplier > 1 && (
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="bg-yellow-500 px-6 py-3 rounded-xl text-center"
                    >
                        <div className="text-2xl font-black text-black">{multiplier}X</div>
                        <div className="text-xs text-yellow-900 font-bold">Multiplier</div>
                    </motion.div>
                )}
            </div>

            {/* Game Area */}
            <div
                className="relative bg-slate-800 rounded-3xl border-2 border-slate-700 w-full flex-1 overflow-hidden cursor-none"
                onMouseMove={handleMouseMove}
            >
                {/* Server (Top) */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-slate-700 px-6 py-3 rounded-xl flex items-center gap-3 w-max z-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                        <Zap size={16} />
                    </div>
                    <span className="font-bold text-slate-300">External Server</span>
                </div>

                {/* Combo Display */}
                <AnimatePresence>
                    {showCombo && combo > 1 && (
                        <motion.div
                            initial={{ scale: 0, y: 50 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0, opacity: 0 }}
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-6xl font-black text-yellow-400 pointer-events-none z-20"
                        >
                            {combo}x COMBO!
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Falling Payloads */}
                <AnimatePresence>
                    {payloads.map(payload => {
                        const duration = slowMode ? settings.fallDuration * 1.5 : settings.fallDuration;
                        return (
                            <motion.div
                                key={payload.id}
                                initial={{ top: '10%', left: `${payload.x}%`, opacity: 1, scale: 1 }}
                                animate={{ top: '85%' }}
                                exit={{
                                    opacity: 0,
                                    scale: payload.caught ? 0 : 0.5,
                                    y: payload.caught ? 20 : 0
                                }}
                                transition={{ duration, ease: 'linear' }}
                                onAnimationComplete={() => {
                                    if (payload.caught === null) handleCatch(payload);
                                }}
                                className="absolute -translate-x-1/2 z-10"
                            >
                                {payload.isPowerUp ? (
                                    <motion.div
                                        animate={{ rotate: [0, 10, -10, 0] }}
                                        transition={{ repeat: Infinity, duration: 0.5 }}
                                        className={`${POWER_UPS.find(p => p.type === payload.powerUpType)?.color} rounded-full w-14 h-14 flex items-center justify-center text-2xl shadow-lg border-4 border-white/50`}
                                    >
                                        {POWER_UPS.find(p => p.type === payload.powerUpType)?.emoji}
                                    </motion.div>
                                ) : (
                                    <div className={`${PAYLOAD_TYPES.find(p => p.type === payload.type)?.color || 'bg-purple-500'} rounded-xl p-3 shadow-lg transform rotate-12`}>
                                        <div className="text-xs font-bold text-white">{payload.type}</div>
                                        <div className="text-[10px] text-white/70 font-mono mt-1">
                                            {JSON.stringify(payload.data).substring(0, 20)}...
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        );
                    })}
                </AnimatePresence>

                {/* Catcher */}
                <motion.div
                    className="absolute bottom-8 -translate-x-1/2 z-20"
                    style={{ left: `${catcherX}%` }}
                >
                    <div className="text-6xl">🧤</div>
                </motion.div>

                {/* Catch Zone Guide */}
                <div
                    className="absolute bottom-0 h-2 bg-green-500/30"
                    style={{ left: `${catcherX - settings.catcherWidth}%`, width: `${settings.catcherWidth * 2}%` }}
                />

                {/* Overlay */}
                {!isPlaying && (
                    <div className="absolute inset-0 bg-slate-900/80 flex items-center justify-center z-30">
                        <div className="text-center">
                            {gameOver ? (
                                <>
                                    <div className="text-4xl mb-4">{score > highScore - 10 ? '🏆' : '😵'}</div>
                                    <h2 className="text-2xl font-bold text-white mb-2">Game Over!</h2>
                                    <p className="text-slate-400 mb-2">Final Score: {score}</p>
                                    {score >= highScore && score > 0 && (
                                        <p className="text-yellow-400 font-bold mb-4">🎉 New High Score!</p>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="text-6xl mb-4">🧤</div>
                                    <h2 className="text-2xl font-bold text-white mb-2">Ready to Catch?</h2>
                                    <p className="text-slate-400 mb-4">Move mouse to control glove</p>
                                </>
                            )}
                            <button
                                onClick={startGame}
                                className="bg-orange-600 hover:bg-orange-500 text-white font-bold px-8 py-3 rounded-xl transition-all"
                            >
                                {gameOver ? 'Try Again' : 'Start Game'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-4 flex justify-center gap-4 flex-wrap">
                {POWER_UPS.map(p => (
                    <div key={p.type} className="flex items-center gap-2 text-sm text-slate-400">
                        <span className="text-xl">{p.emoji}</span>
                        <span>{p.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const WebhookCatcher = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        if (!completed) {
            setCompleted(true);
            completeModule('webhooks', 100);
        }
    };

    return (
        <LessonWrapper
            moduleName="Webhook Catcher"
            moduleEmoji="🧤"
            moduleColor="orange"
            lessons={LESSONS}
            completed={completed}
            activityLabel="The Game"
        >
            <WebhookGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default WebhookCatcher;
